import {z} from 'zod';

import {is, isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {ProductRequestBody} from '@dms/api/core';
import i18n from '@dms/i18n';

import {Nullish} from 'shared/web';

const MAX_NAME_LENGTH = 255;
const MIN_PRICE = 0.01;

// The previous `mixed().test()` skipped the check for any falsy amount, which includes
// the number 0 that the price inputs emit - keep that hole so a zero price stays savable.
const isBelowMinPrice = (amount: string | number | Nullish) =>
  Boolean(amount) && parseFloat(String(amount)) < MIN_PRICE;

// `z.custom` does no type checking, so both casts must tolerate a non-string rather than
// throwing inside the resolver.
const castRequiredString = (value: unknown) => (is(String, value) ? value.trim() : '');

const castOptionalString = (value: unknown) => {
  if (!is(String, value) || value === '') {
    return null;
  }

  return value.trim();
};

/**
 * `z.custom` instead of `z.object` because the submitted values are merged into the
 * request body as-is: the form also carries `vatType`, `vatRate`, `id` and `createdAt`,
 * which a `z.object` would strip. The transform only casts the two text fields;
 * everything else is validation.
 *
 * Both selling amounts are required; the purchase amounts are optional but, like the
 * selling ones, must reach the minimum price once they hold a truthy value.
 *
 * The form is typed as the request body itself, which predates this migration and is what
 * `TODO: Refactor this - [T20-17714]` on the component refers to; inferring the type here
 * would first mean giving the form a type of its own.
 */
export const ProductCatalogItemFormSchema: z.ZodSchema<ProductRequestBody> = z
  .custom<ProductRequestBody>()
  .transform((values) => ({
    ...values,
    name: castRequiredString(values.name),
    accountingContextCustomItemCode: castOptionalString(values.accountingContextCustomItemCode),
  }))
  .superRefine((values, ctx) => {
    if (isNilOrEmpty(values.name)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['name'],
        message: i18n.t('general.validations.fieldIsRequired'),
      });
    }

    if (values.name.length > MAX_NAME_LENGTH) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['name'],
        message: i18n.t('general.errors.string.max', {max: MAX_NAME_LENGTH}),
      });
    }

    const amounts = [
      {
        path: 'priceWithVat',
        purchase: values.purchasePrice?.priceWithVat?.amount,
        selling: values.sellingPrice?.priceWithVat?.amount,
      },
      {
        path: 'priceWoVat',
        purchase: values.purchasePrice?.priceWoVat?.amount,
        selling: values.sellingPrice?.priceWoVat?.amount,
      },
    ];

    amounts.forEach(({path, purchase, selling}) => {
      if (isBelowMinPrice(purchase)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['purchasePrice', path, 'amount'],
          message: i18n.t('general.notifications.priceMin', {min: MIN_PRICE}),
        });
      }

      if (isNil(selling)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['sellingPrice', path, 'amount'],
          message: i18n.t('general.notifications.fieldIsRequired'),
        });

        return;
      }

      if (isBelowMinPrice(selling)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['sellingPrice', path, 'amount'],
          message: i18n.t('general.notifications.priceMin', {min: MIN_PRICE}),
        });
      }
    });
  });
